import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import sidebarLogo from "../../../../assets/img/square-logo-v2.png";
import { AUTOMATION_TIMELINE, PRESENT_SCREEN_MOCK } from "../../landingContent";
import HandDrawnUnderline from "../HandDrawnUnderline";
import PresentScreenMockFrame from "./PresentScreenMockFrame";
import PresentScreenHeroCallouts from "./PresentScreenHeroCallouts";
import PresentScreenHeroPreviewSpeed from "./PresentScreenHeroPreviewSpeed";
import PresentScreenHeroPreviewPlayback from "./PresentScreenHeroPreviewPlayback";
import {
  DEFAULT_HERO_PREVIEW_SPEED_ID,
  getHeroPreviewSpeedMultiplier,
  scaleHeroPreviewDelayMs,
} from "../../heroPreviewSpeed";
import { FULLSTORY_ELEMENTS } from "utils/fullstoryElements";

const HERO_AUTOMATION_EVENT_IDS = [
  "warmup-timer",
  "warmup-reminder",
  "birthday",
  "agenda-progress",
  "agenda-widgets",
  "cleanup",
  "next-class",
];

const HERO_AUTOMATION_ADVANCE_DELAYS_MS = {
  "warmup-timer": 6200,
  "warmup-reminder": 6200,
  birthday: 6200,
  "agenda-progress": 6200,
  "agenda-widgets": 6200,
  cleanup: 7200,
  "next-class": 8200,
  "browser-window": 10000,
};

// The mobile hero shows the exact desktop mock scaled down. The canvas keeps its
// fixed desktop width; the wrapper measures the available width to derive the scale.
const HERO_MOCK_DESKTOP_WIDTH = 1080;
const HERO_MOCK_MOBILE_QUERY = "(max-width: 767px)";

const getIsMobileScaledViewport = () => (
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(HERO_MOCK_MOBILE_QUERY).matches
);

const PresentScreenMockSection = ({ previewContainerRef }) => {
  const [activeAutomationIndex, setActiveAutomationIndex] = useState(0);
  const [isMobileScaled, setIsMobileScaled] = useState(getIsMobileScaledViewport);
  const [mobileScale, setMobileScale] = useState(null);
  const previewScaleRef = useRef(null);
  const [previewSpeedId, setPreviewSpeedId] = useState(DEFAULT_HERO_PREVIEW_SPEED_ID);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(true);
  const [isPreviewInView, setIsPreviewInView] = useState(false);
  const [previewElement, setPreviewElement] = useState(null);
  const introRef = useRef(null);
  const previewSpeedMultiplier = getHeroPreviewSpeedMultiplier(previewSpeedId);
  const isAutomationLoopActive = isPreviewPlaying;
  const shouldAnimatePreview = isAutomationLoopActive && isPreviewInView;

  const setPreviewNode = (node) => {
    if (previewContainerRef) {
      previewContainerRef.current = node;
    }
    setPreviewElement(node);
  };
  const heroAutomationEvents = HERO_AUTOMATION_EVENT_IDS
    .map((id) => {
      const timelineEventId = id === "agenda-widgets" ? "agenda-progress" : id;
      const automationIndex = AUTOMATION_TIMELINE.events.findIndex((event) => event.id === timelineEventId);

      if (automationIndex < 0) {
        return null;
      }

      return {
        ...AUTOMATION_TIMELINE.events[automationIndex],
        id,
        label: id === "agenda-widgets" ? "Helpful widgets" : AUTOMATION_TIMELINE.events[automationIndex].label,
        automationIndex,
        heroPreviewMode: id === "agenda-widgets" ? "widgets" : id === "agenda-progress" ? "agenda" : undefined,
      };
    })
    .filter(Boolean);
  const browserWindowAutomationEvent = heroAutomationEvents[heroAutomationEvents.length - 1];

  if (browserWindowAutomationEvent) {
    heroAutomationEvents.push({
      ...browserWindowAutomationEvent,
      id: "browser-window",
      label: "Browser window",
      isBrowserWindowStep: true,
    });
  }

  const activeEvent = heroAutomationEvents[activeAutomationIndex] || heroAutomationEvents[0];
  const isBrowserWindowStep = Boolean(activeEvent?.isBrowserWindowStep);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(HERO_MOCK_MOBILE_QUERY);
    const handleChange = (event) => setIsMobileScaled(event.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useLayoutEffect(() => {
    const node = previewScaleRef.current;

    if (!isMobileScaled || !node) {
      return undefined;
    }

    const updateScale = () => {
      if (node.clientWidth > 0) {
        setMobileScale(node.clientWidth / HERO_MOCK_DESKTOP_WIDTH);
      }
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale, { passive: true });
      return () => window.removeEventListener("resize", updateScale);
    }

    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobileScaled]);

  useEffect(() => {
    if (!previewElement) {
      return undefined;
    }

    const updatePreviewInView = () => {
      const rect = previewElement.getBoundingClientRect();
      setIsPreviewInView(rect.bottom > 0 && rect.top < window.innerHeight);
    };

    updatePreviewInView();

    if (!("IntersectionObserver" in window)) {
      window.addEventListener("scroll", updatePreviewInView, { passive: true });
      window.addEventListener("resize", updatePreviewInView, { passive: true });

      return () => {
        window.removeEventListener("scroll", updatePreviewInView);
        window.removeEventListener("resize", updatePreviewInView);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsPreviewInView(entry.isIntersecting);
      },
      { threshold: 0.01, rootMargin: "120px 0px 120px 0px" }
    );

    observer.observe(previewElement);
    window.addEventListener("scroll", updatePreviewInView, { passive: true });
    window.addEventListener("resize", updatePreviewInView, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updatePreviewInView);
      window.removeEventListener("resize", updatePreviewInView);
    };
  }, [previewElement]);

  useEffect(() => {
    if (!isAutomationLoopActive || heroAutomationEvents.length <= 1) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActiveAutomationIndex((currentIndex) => (
        currentIndex + 1 >= heroAutomationEvents.length ? 0 : currentIndex + 1
      ));
    }, scaleHeroPreviewDelayMs(
      HERO_AUTOMATION_ADVANCE_DELAYS_MS[activeEvent?.id] || 6200,
      previewSpeedMultiplier
    ));

    return () => window.clearTimeout(timeoutId);
  }, [
    activeAutomationIndex,
    activeEvent?.id,
    heroAutomationEvents.length,
    isAutomationLoopActive,
    previewSpeedMultiplier,
  ]);

  const handlePreviewSpeedChange = (nextSpeedId) => {
    setPreviewSpeedId(nextSpeedId);
    setActiveAutomationIndex(0);
  };

  const handlePreviewPlaybackToggle = () => {
    setIsPreviewPlaying((isPlaying) => !isPlaying);
  };

  return (
    <section className="landing-v2-present-hero" aria-labelledby="present-hero-heading">
      <div ref={introRef} className="landing-v2__container landing-v2-present-hero__intro">
        <h1 id="present-hero-heading" className="landing-v2-present-hero__title">
          <HandDrawnUnderline sequence={1}>{PRESENT_SCREEN_MOCK.headlineHighlight}</HandDrawnUnderline>
          {PRESENT_SCREEN_MOCK.headlineRest}
          <br />
          {PRESENT_SCREEN_MOCK.headlineSecondLinePrefix}
          <HandDrawnUnderline sequence={2}>{PRESENT_SCREEN_MOCK.headlineSecondLineHighlight}</HandDrawnUnderline>.
        </h1>
        <p className="landing-v2-present-hero__subtitle">{PRESENT_SCREEN_MOCK.subheadline}</p>

        <div className="landing-v2-present-hero__cta-block">
          <Link
            to="/auth/register"
            className="landing-v2__btn landing-v2__btn--primary landing-v2__btn--full-mobile"
            data-fs-element={FULLSTORY_ELEMENTS.landing.heroTrial}
          >
            <i className="fa fa-rocket landing-v2__btn-icon" aria-hidden="true" />
            {PRESENT_SCREEN_MOCK.primaryCta}
          </Link>
          <p className="landing-v2-present-hero__cta-subtext">{PRESENT_SCREEN_MOCK.ctaSubtext}</p>
        </div>
      </div>

      <div className="landing-v2__container landing-v2-present-hero__preview-container">
        <div ref={previewScaleRef} className="landing-v2-present-hero__preview-scale">
          <div
            ref={setPreviewNode}
            id="hero-demo"
            className={`landing-v2-present-hero__automation-preview${
              isMobileScaled ? " landing-v2-present-hero__automation-preview--scaled" : ""
            }`}
            style={isMobileScaled && mobileScale ? { "--hero-mock-scale": String(mobileScale) } : undefined}
            aria-label="Condensed automation preview showing timers, reminders, agenda progress, birthday announcements, and widgets"
          >
            <div
              className={`landing-v2-present-hero__browser-stage${
                isBrowserWindowStep ? " landing-v2-present-hero__browser-stage--active" : ""
              }`}
            >
              <div className="landing-v2-present-hero__browser-chrome" aria-hidden="true">
                <div className="landing-v2-present-hero__browser-tabs">
                  <div className="landing-v2-present-hero__browser-window-controls">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="landing-v2-present-hero__browser-tab landing-v2-present-hero__browser-tab--active">
                    <img src={sidebarLogo} alt="" />
                    <span>Lesson Launchpad</span>
                  </div>
                  <div className="landing-v2-present-hero__browser-tab-new">+</div>
                </div>
                <div className="landing-v2-present-hero__browser-toolbar">
                  <div className="landing-v2-present-hero__browser-nav">
                    <span className="landing-v2-present-hero__browser-nav-btn landing-v2-present-hero__browser-nav-btn--disabled">
                      <i className="fa fa-chevron-left" />
                    </span>
                    <span className="landing-v2-present-hero__browser-nav-btn landing-v2-present-hero__browser-nav-btn--disabled">
                      <i className="fa fa-chevron-right" />
                    </span>
                    <span className="landing-v2-present-hero__browser-nav-btn">
                      <i className="fa fa-refresh" />
                    </span>
                  </div>
                  <div className="landing-v2-present-hero__browser-address">
                    <i className="fa fa-lock" />
                    <span>www.lessonlaunchpad.com</span>
                  </div>
                  <div className="landing-v2-present-hero__browser-toolbar-pills" aria-hidden="true">
                    <span className="landing-v2-present-hero__browser-pill landing-v2-present-hero__browser-pill--wide" />
                    <span className="landing-v2-present-hero__browser-pill" />
                    <span className="landing-v2-present-hero__browser-pill" />
                  </div>
                  <div className="landing-v2-present-hero__browser-actions">
                    <span className="landing-v2-present-hero__browser-action-btn">
                      <i className="fa fa-star-o" />
                    </span>
                    <span className="landing-v2-present-hero__browser-action-btn">
                      <i className="fa fa-ellipsis-v" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="landing-v2-present-hero__browser-screen">
                <PresentScreenMockFrame
                  variant="timeline"
                  activeAutomationIndex={activeEvent?.automationIndex || 0}
                  ariaLabel="Animated Lesson Launchpad Present Screen preview"
                  isAnimationPaused={!shouldAnimatePreview}
                  disableFastForwardEffects
                  heroPreviewMode={activeEvent?.heroPreviewMode}
                />
              </div>
            </div>

            <PresentScreenHeroPreviewPlayback
              isPlaying={isPreviewPlaying}
              onToggle={handlePreviewPlaybackToggle}
            />

            <PresentScreenHeroPreviewSpeed
              activeSpeedId={previewSpeedId}
              onSpeedChange={handlePreviewSpeedChange}
            />

            <PresentScreenHeroCallouts
              activeEventId={activeEvent?.id}
              isVisible={shouldAnimatePreview}
              previewSpeedMultiplier={previewSpeedMultiplier}
            />

            <div className="landing-v2-present-hero__preview-badge" aria-hidden="true">
              <i className="fa fa-bolt" />
              Live automation preview
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PresentScreenMockSection;
